import { Input, Modal, Table, Tag } from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { getSegmentCustomers } from "../../../RestServices/authService";

const SEGMENT_LABELS = {
  subscribed: "Subscribed Customers",
  inactive: "Inactive Customers",
  other: "Other Customers",
};

/**
 * Paginated + searchable customer list for a dashboard donut segment.
 * All filtering happens server-side (segmentCustomers API).
 */
const SegmentCustomersModal = ({ visible, segment, onCancel, token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const fetchIdRef = useRef(0);

  // Debounce typing → search
  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // Reset when opened with a (new) segment
  useEffect(() => {
    if (visible) {
      setSearchInput("");
      setSearch("");
      setPagination({ current: 1, pageSize: 20, total: 0 });
    }
  }, [visible, segment]);

  useEffect(() => {
    if (!visible || !segment) return;
    const fetchId = ++fetchIdRef.current;
    (async () => {
      try {
        setLoading(true);
        const data = await getSegmentCustomers(token, {
          segment,
          search: search || undefined,
          page: pagination.current,
          limit: pagination.pageSize,
        });
        if (fetchId !== fetchIdRef.current) return;
        const docs = data.customers?.docs || [];
        setRows(
          docs.map((c) => ({
            key: c._id,
            name: c.name,
            phoneNumber: c.phoneNumber,
            city: c.city,
            type: c.profileDetails?.type || "N/A",
            business: c.profileDetails?.businessNature || "—",
            lastOpen: c.lastProfileFetched,
            createdAt: c.createdAt,
          }))
        );
        setPagination((p) => ({ ...p, total: data.customers?.totalDocs || 0 }));
      } catch (err) {
        console.error("Failed to fetch segment customers", err);
      } finally {
        if (fetchId === fetchIdRef.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, segment, search, pagination.current, pagination.pageSize]);

  const columns = [
    {
      title: "Sr.",
      width: 60,
      render: (t, r, i) => (pagination.current - 1) * pagination.pageSize + i + 1,
    },
    { title: "Name", dataIndex: "name", render: (v) => v || "N/A" },
    { title: "Phone", dataIndex: "phoneNumber" },
    { title: "City", dataIndex: "city", render: (v) => v || "N/A" },
    {
      title: "Profile",
      dataIndex: "type",
      render: (v) => (
        <Tag color={v === "UPGRADED" ? "green" : "default"}>{v}</Tag>
      ),
    },
    { title: "Business", dataIndex: "business" },
    {
      title: "Last App Open",
      dataIndex: "lastOpen",
      render: (v) =>
        v ? (
          <span title={moment(v).format("LLL")}>{moment(v).fromNow()}</span>
        ) : (
          <Tag>Never</Tag>
        ),
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "N/A"),
    },
  ];

  return (
    <Modal
      title={SEGMENT_LABELS[segment] || "Customers"}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      style={{ top: 30 }}
    >
      <Input.Search
        placeholder="Search by name or phone number"
        allowClear
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onSearch={(value) => {
          setPagination((p) => ({ ...p, current: 1 }));
          setSearchInput(value || "");
          setSearch(value || "");
        }}
        style={{ marginBottom: 16, maxWidth: 360 }}
      />
      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} customers`,
        }}
        onChange={(p) =>
          setPagination({ current: p.current, pageSize: p.pageSize, total: pagination.total })
        }
        size="small"
        scroll={{ x: "max-content" }}
      />
    </Modal>
  );
};

export default SegmentCustomersModal;
